import { DateTime } from "luxon";
import {
    dateTimeToDateLocalFormFieldString,
    dateTimeToDateTimeLocalFormFieldString,
} from "./dateUtils";
import {
    findThingSchema,
    getAttribute,
    isTimeSeriesAttribute,
    isArrayAttribute,
    getRelationOptionDisplayValue,
    isGlobalRelationAttribute,
} from "./thingUtils";
import {
    findReadoutGroupById,
    findTaskGroupById,
    findTaskInTaskSchedule,
    getReadoutGroupAttributes,
    getTaskDisplayNameInTaskSchedule,
} from "@/lib/taskUtils";
import _ from "lodash";
import { MeasurementValueCoercer } from "@/services/measurements/measurementValueCoercer";

export function findMeasurementForAttributeName(attributeName, measurements) {
    return (
        measurements.find((measurement) => {
            return measurement.attributeName === attributeName;
        }) || null
    );
}

export function getMeasurementDisplayValue(
    value,
    attributeName,
    thingSchema,
    experiment,
    protocol
) {
    const attribute = getAttribute(thingSchema, attributeName);
    const measurementValueCoercer = MeasurementValueCoercer.buildForExperiment(
        protocol,
        experiment
    );

    if (!attribute) {
        throw new Error(
            `Could not find attribute with name ${attributeName} in ${thingSchema.displayName}`
        );
    }

    return measurementValueCoercer.getMeasurementDisplayValue(attribute, value);
}

export function getGlobalRelationDisplayValue(registrySchema, attribute, value) {
    if (_.isNil(value)) {
        return value;
    }

    if (isGlobalRelationAttribute(attribute)) {
        const allRelationOptions = registrySchema.globalRelationOptions;
        return getRelationOptionDisplayValue(attribute, allRelationOptions, value);
    } else if (isArrayAttribute(attribute) && _.isArray(value)) {
        return value.join(",");
    } else {
        return value;
    }
}

export function findThingMeasurementSetForThingId(userMeasurementSet, thingId) {
    return userMeasurementSet.thingMeasurementSets.find((thingMeasurementSet) => {
        return thingMeasurementSet.thingId === thingId;
    });
}

export function getMeasurementCollectionDateTime(measurement, experiment) {
    if (!measurement) {
        return null;
    }
    const collectionDateTimeUTCISOString = measurement.collectionDateTime;
    return getCollectionDateTime(collectionDateTimeUTCISOString, experiment);
}

/**
 * TODO: it would be cleaner to map the collectionDateTime to a luxon DateTime closer to the API call so
 *  this conversion is done once rather than by every component that needs it.
 */
export function getCollectionDateTime(collectionDateTimeISOString, experiment) {
    return DateTime.fromISO(collectionDateTimeISOString, { zone: experiment.siteTimeZone });
}

/**
 * Return the properties used to build a collection date time field, depending on time/date settings of the
 * ReadoutGroup
 */
export function buildCollectionDateTimeFieldConfig(collectionDateTime, readoutGroup, experiment) {
    // TODO: for now, we're assuming it's ok to collect date only for multiple task files, which do
    // not have a readout group. We may need a better heuristic.
    if (readoutGroup && readoutGroup.recordTimeWithMeasurements) {
        return {
            dateFieldType: "datetime-local",
            editCollectionDateTimeISOString: dateTimeToDateTimeLocalFormFieldString(
                collectionDateTime,
                experiment
            ),
            collectionDateTimeLabel: "Collection Date/Time",
        };
    } else {
        return {
            dateFieldType: "date",
            editCollectionDateTimeISOString: dateTimeToDateLocalFormFieldString(
                collectionDateTime,
                experiment
            ),
            collectionDateTimeLabel: "Collection Date",
        };
    }
}

export function findSummaryOneTimeMeasurementSetInSummaryForThingId(
    summaryOneTimeMeasurementSets,
    thingId
) {
    return summaryOneTimeMeasurementSets.find((thingMeasurementSet) => {
        return thingMeasurementSet.thingId === thingId;
    });
}

export function findSummaryTimeSeriesMeasurementSetInSummaryForThingId(
    summaryTimeSeriesMeasurementSets,
    thingId
) {
    return summaryTimeSeriesMeasurementSets.find((thingMeasurementSet) => {
        return thingMeasurementSet.thingId === thingId;
    });
}

export function findSummaryOneTimeMeasurementForThingAndAttributeName({
    summaryOneTimeMeasurementSets,
    thing,
    attributeName,
}) {
    if (!summaryOneTimeMeasurementSets) {
        return null;
    }

    const thingMeasurementSet = findSummaryOneTimeMeasurementSetInSummaryForThingId(
        summaryOneTimeMeasurementSets,
        thing.id
    );
    if (!thingMeasurementSet) {
        return null;
    }

    const measurementForAttribute = thingMeasurementSet.latestUserMeasurements.find(
        (measurement) => {
            return measurement.attributeName === attributeName;
        }
    );

    if (!measurementForAttribute) {
        return null;
    }

    return measurementForAttribute;
}

export function findSummaryTimeSeriesMeasurementForThingAndAttributeName({
    summaryTimeSeriesMeasurementSets,
    thing,
    attributeName,
}) {
    if (!summaryTimeSeriesMeasurementSets) {
        return null;
    }

    const thingMeasurementSet = findSummaryTimeSeriesMeasurementSetInSummaryForThingId(
        summaryTimeSeriesMeasurementSets,
        thing.id
    );

    if (!thingMeasurementSet) {
        return null;
    }

    const measurementForAttribute = thingMeasurementSet.latestUserMeasurements.find(
        (measurement) => {
            return measurement.attributeName === attributeName;
        }
    );

    if (!measurementForAttribute) {
        return null;
    }

    return measurementForAttribute;
}

export function findThingMeasurementSet(userMeasurementSet, thing) {
    if (!userMeasurementSet) {
        return null;
    }

    return findThingMeasurementSetForThingId(userMeasurementSet, thing.id);
}

export function findUserMeasurementForThingAndAttributeName(
    userMeasurementSet,
    thing,
    attributeName
) {
    const thingMeasurementSet = findThingMeasurementSet(userMeasurementSet, thing);
    if (!thingMeasurementSet) {
        return null;
    }

    return findMeasurementForAttributeName(attributeName, thingMeasurementSet.measurements);
}

export function isSummaryMeasurement(measurement) {
    return "dataCollectionTaskId" in measurement;
}

/**
 * Fetch all the objects needed to render the data entry page for a data collection task.
 */
export async function findTaskScheduleObjects({ dataCollectionTask, protocol, taskSchedule }) {
    const readoutGroup = findReadoutGroupById(
        dataCollectionTask.readoutGroupId,
        taskSchedule.readoutGroups
    );

    const thingSchema = findThingSchema(readoutGroup.thingSchemaId, protocol);
    const taskGroup = findTaskGroupById(dataCollectionTask.taskGroupId, taskSchedule.taskGroups);

    const readoutGroupAttributes = getReadoutGroupAttributes(readoutGroup, thingSchema);

    return {
        taskGroup,
        readoutGroup,
        readoutGroupAttributes,
        thingSchema,
    };
}

export function getLatestMeasurementForThingAttribute({
    thingSchema,
    thing,
    attribute,
    currentUserMeasurementSet,
    summaryOneTimeMeasurementSets,
    summaryTimeSeriesMeasurementSets,
}) {
    const userMeasurement = findUserMeasurementForThingAndAttributeName(
        currentUserMeasurementSet,
        thing,
        attribute.name
    );

    let summaryMeasurement;
    if (isTimeSeriesAttribute(thingSchema, attribute)) {
        const summaryTimeSeriesMeasurement =
            findSummaryTimeSeriesMeasurementForThingAndAttributeName({
                summaryTimeSeriesMeasurementSets,
                thing,
                attributeName: attribute.name,
            });

        summaryMeasurement = summaryTimeSeriesMeasurement;
    } else {
        const summaryOneTimeMeasurement = findSummaryOneTimeMeasurementForThingAndAttributeName({
            summaryOneTimeMeasurementSets,
            thing,
            attributeName: attribute.name,
        });

        summaryMeasurement = summaryOneTimeMeasurement;
    }

    if (userMeasurement && summaryMeasurement) {
        // compare them, show the "latest". If the user provides a value, then it likely won't be
        // aggregated into the summary by the time the user hits "save" and shows this view again.
        // If we always show the summary value, it will look like the form didn't work. If we always
        // show the user's measurement value, it won't show other user's values. Comparing the
        // timestamp is effectively what the aggregator does, so this _should_ work in most cases.
        return compareMeasurements({
            userMeasurement,
            summaryMeasurement: summaryMeasurement,
        });
    } else if (userMeasurement) {
        // If there's no summary measurement but a user measurement, show the user measurement. Note
        // that we're adding the dataCollectionTaskId to the user measurement, because user measurements
        // do not have a dataCollectionTaskId individually, while summary measurements are associated
        // to individual tasks.
        return userMeasurement;
    } else if (summaryMeasurement) {
        return summaryMeasurement;
    } else {
        return null;
    }
}

function compareMeasurements({ userMeasurement, summaryMeasurement }) {
    const userMeasurementDate = new Date(userMeasurement.timestamp);
    const summaryMeasurementDate = new Date(summaryMeasurement.timestamp);
    if (userMeasurementDate >= summaryMeasurementDate) {
        return userMeasurement;
    } else {
        return summaryMeasurement;
    }
}

export function getEditabilityForMeasurement({
    measurement,
    dataCollectionTask,
    experiment,
    taskSchedule,
}) {
    if (measurement && isSummaryMeasurement(measurement)) {
        // If this is a summary measurement, then only allow editing if the value is from this task.
        // This prevents trying to provide a new value from an existing user measurement in a later
        // task. The default task only provides "initial" values, and the later value will "win" in
        // the aggregator process, resulting the summary value not changing.
        if (measurement.dataCollectionTaskId === dataCollectionTask.id) {
            return {
                editable: true,
            };
        } else {
            const measurementTask = findTaskInTaskSchedule(
                measurement.dataCollectionTaskId,
                taskSchedule
            );
            const experimentStartDate = experiment.day0;
            const taskName = getTaskDisplayNameInTaskSchedule(
                measurementTask,
                taskSchedule,
                experimentStartDate
            );
            // Note: only summary measurement would have a userId, but this should always be a summary measurement
            return {
                editable: false,
                disabledMessage: `${measurement.userId} - task ${taskName}`,
            };
        }
    } else {
        // if this is a user measurement, it's for this task, and it's always editable in the admin
        return {
            editable: true,
        };
    }
}

export function buildMeasurementsImportMessage({ skippedRows, skippedAttributes, skippedSheets }) {
    const skippedSheetsMessage = arrayToString(skippedSheets, (joined) => {
        return `${skippedSheets.length} sheet(s) skipped due to collection dates omitted: ${joined}.`;
    });

    const skippedThingsMessage = arrayToString(skippedRows, (joined) => {
        return `${skippedRows.length} row(s) skipped for imported sheets: ${joined}`;
    });

    const skippedAttributesMessage = arrayToString(skippedAttributes, (joined) => {
        return `${skippedAttributes.length} column(s) skipped based on readout group configuration: ${joined}.`;
    });

    const msgs = [skippedSheetsMessage, skippedThingsMessage, skippedAttributesMessage].join("\n");

    return `Imported measurements.${msgs}`;
}

function arrayToString(array, fn) {
    if (!_.isNil(array) && array.length > 0) {
        return fn(array.join(", "));
    } else {
        return "";
    }
}

export class MeasurementsHelper {
    constructor({
        experiment,
        protocol,
        thingSchema,
        summaryOneTimeMeasurementSets,
        summaryTimeSeriesMeasurementSets,
        userMeasurementSet,
    }) {
        this.experiment = experiment;
        this.protocol = protocol;
        this.thingSchema = thingSchema;
        this.summaryOneTimeMeasurementSets = summaryOneTimeMeasurementSets;
        this.summaryTimeSeriesMeasurementSets = summaryTimeSeriesMeasurementSets;

        if (userMeasurementSet) {
            this.userMeasurementSet = _.cloneDeep(userMeasurementSet);
        } else {
            this.userMeasurementSet = {
                thingMeasurementSets: [],
            };
        }
    }

    getLatestMeasurement({ thing, attribute }) {
        return (
            getLatestMeasurementForThingAttribute({
                thingSchema: this.thingSchema,
                thing,
                attribute,
                currentUserMeasurementSet: this.userMeasurementSet,
                summaryOneTimeMeasurementSets: this.summaryOneTimeMeasurementSets,
                summaryTimeSeriesMeasurementSets: this.summaryTimeSeriesMeasurementSets,
            }) || null
        );
    }

    isMeasurementValueUpdated({ thing, attribute, value, collectionDateTime }) {
        const currentMeasurement = this.getLatestMeasurement({
            thing,
            attribute,
        });

        if (_.isNil(currentMeasurement)) {
            return true;
        }

        const currentValue = currentMeasurement.value;

        const currentCollectionDateTime = getMeasurementCollectionDateTime(
            currentMeasurement,
            this.experiment
        );

        if (!_.isNil(collectionDateTime) && !collectionDateTime.equals(currentCollectionDateTime)) {
            return true;
        }

        return value !== currentValue;
    }

    updateMeasurement({ thing, attribute, value, collectionDateTime }) {
        const thingMeasurementSet = findThingMeasurementSetForThingId(
            this.userMeasurementSet,
            thing.id
        );

        if (thingMeasurementSet) {
            this._updateExistingThingMeasurementSet({
                thingMeasurementSet,
                attribute,
                value,
                collectionDateTime,
            });
        } else {
            this._addNewThingMeasurementSet({ thing, attribute, value, collectionDateTime });
        }
    }

    _updateExistingThingMeasurementSet({
        thingMeasurementSet,
        attribute,
        value,
        collectionDateTime,
    }) {
        const measurement = findMeasurementForAttributeName(
            attribute.name,
            thingMeasurementSet.measurements
        );

        if (measurement) {
            measurement.value = value;
            measurement.collectionDateTime = collectionDateTime;
            measurement.timestamp = new Date().toISOString();
        } else {
            thingMeasurementSet.measurements.push({
                attributeName: attribute.name,
                value,
                collectionDateTime,
                timestamp: new Date().toISOString(),
            });
        }
    }

    _addNewThingMeasurementSet({ thing, attribute, value, collectionDateTime }) {
        this.userMeasurementSet.thingMeasurementSets.push({
            thingId: thing.id,
            measurements: [
                {
                    attributeName: attribute.name,
                    value,
                    collectionDateTime,
                    timestamp: new Date().toISOString(),
                },
            ],
        });
    }

    /**
     * Update multiple measurements for a Thing
     */
    replaceThingMeasurements({ thing, measurements, collectionDateTime }) {
        let updatedMeasurementValue = false;

        const attributeNamesToKeep = [];

        measurements.forEach((measurement) => {
            const schema = findThingSchema(thing.thingSchemaId, this.protocol);
            const attributeName = measurement.attributeName;
            const attribute = getAttribute(schema, attributeName);
            const value = measurement.value;

            // Note: this _only_ checks whether the value has changed, it does _not_ check whether
            // the collectionDateTime has changed. This is because the collectionDateTime is set
            // for all measurements in the form; if the value hasn't changed, the user's intent
            // is not to update the collectionDateTime on values that they did not change.
            const isUpdated = this.isMeasurementValueUpdated({
                thing,
                attribute,
                value,
            });

            if (isUpdated) {
                this.updateMeasurement({
                    thing,
                    attribute,
                    value,
                    collectionDateTime,
                });
                updatedMeasurementValue = true;
            }

            // note we're keeping the measurement even if the value didn't change.
            attributeNamesToKeep.push(attribute.name);
        });

        const removed = this._removeMeasurements({
            thing,
            attributeNamesToKeep,
        });

        return updatedMeasurementValue || removed;
    }

    /**
     * Remove unused/unreferenced measurements
     * @param thing
     * @param attributeNamesToKeep
     * @return {boolean} true if any measurements were removed
     * @private
     */
    _removeMeasurements({ thing, attributeNamesToKeep }) {
        function isMeasurementToKeep(measurement) {
            return attributeNamesToKeep.some(
                (attributeNameToKeep) => attributeNameToKeep === measurement.attributeName
            );
        }

        // remove any measurements that were not provided (they were removed)
        const thingMeasurementSet = findThingMeasurementSetForThingId(
            this.userMeasurementSet,
            thing.id
        );

        const existingAttributes = thingMeasurementSet.measurements.map(
            (measurement) => measurement.attributeName
        );

        if (_.difference(existingAttributes, attributeNamesToKeep).length > 0) {
            thingMeasurementSet.measurements =
                thingMeasurementSet.measurements.filter(isMeasurementToKeep);
            return true;
        } else {
            return false;
        }
    }
}
